/**
 * Dev-sync J4 — the Checkpoint-4 cascade commit helper.
 *
 * Port of legacy `static/mvc/cascade/cascade_warning.js` guardSelect (90
 * lines, of which this is the ~15 that do anything — survey §B1). Its own
 * docstring (:5-11): the historical cascade-impact confirmation popup "is
 * gone... So this just commits." ★★ THERE IS NO PREVIEW UI TO PORT — every
 * legacy call site still passes `title`/`headline`/`onCancel` and none are
 * read (`onCancel` is never invoked; `?preview=true` has zero frontend
 * consumers). Do not add a confirmation dialog here.
 *
 * Behavior (cascade_warning.js:51-85):
 *  - POST JSON (CSRF) to one of the 8 select/commit_air_conditions
 *    endpoints (urls.py — all accept the resource's own PATCH body verbatim
 *    and return `{updated, cascade: {upstream: [], downstream: []}}`).
 *  - onCommit(resp) runs FIRST, then the promise resolves.
 *  - ★ autoReload is opt-OUT (`!== false`): a NON-EMPTY cascade hard-reloads
 *    the page 150ms later (:73-77) — legacy's deliberate bluntness so the
 *    board re-fetches everything incl. new red highlighting; only the
 *    plenum/evap-pack/UDA manual-edit paths opt out (section.js:226,
 *    component_model.js:180). Keep it — the reload IS the legacy UX.
 *  - on failure: onError(xhr-ish) if provided. Legacy rejects a deferred no
 *    /select caller ever observes (silent total loss, §1 of the legacy
 *    survey); here the promise resolves null instead of rejecting so an
 *    unobserved failure can't surface as an unhandled rejection —
 *    observable behavior identical.
 */

import { getCookie } from 'helpers/index';
import { track } from 'app/analytics';

export interface CascadeResponse {
  updated?: Record<string, unknown>;
  cascade?: { upstream?: unknown[]; downstream?: unknown[] };
  [key: string]: unknown;
}

export interface GuardSelectOpts {
  url: string;
  payload?: Record<string, unknown>;
  onCommit?: (resp: CascadeResponse) => void;
  onError?: (resp: { status: number; body: unknown } | null) => void;
  /** opt-OUT (legacy `opts.autoReload !== false`). */
  autoReload?: boolean;
  /** Set only by a selector's Select (e.g. 'fan'): sends component_selected
   * once the select succeeds. Other guardSelect writes leave it unset. */
  analyticsComponent?: string;
}

export async function guardSelect(opts: GuardSelectOpts): Promise<CascadeResponse | null> {
  const token = getCookie('csrftoken');
  let resp: Response;
  try {
    resp = await fetch(opts.url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...(token ? { 'X-CSRFToken': token } : {}),
      },
      body: JSON.stringify(opts.payload || {}),
    });
  } catch {
    opts.onError?.(null);
    return null;
  }
  if (!resp.ok) {
    let body: unknown = null;
    try {
      body = await resp.json();
    } catch {
      /* non-JSON error body */
    }
    opts.onError?.({ status: resp.status, body });
    return null;
  }
  let data: CascadeResponse = {};
  try {
    data = (await resp.json()) as CascadeResponse;
  } catch {
    /* 204-ish */
  }
  // Before the cascade reload below, so the event is queued first.
  if (opts.analyticsComponent) track('component_selected', { component: opts.analyticsComponent });
  opts.onCommit?.(data);
  const cascade = data && data.cascade;
  const hadCascade =
    !!cascade &&
    ((cascade.upstream ? cascade.upstream.length > 0 : false) ||
      (cascade.downstream ? cascade.downstream.length > 0 : false));
  if (hadCascade && opts.autoReload !== false) {
    window.setTimeout(() => {
      try {
        window.location.reload();
      } catch {
        /* noop */
      }
    }, 150);
  }
  return data;
}
